import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaClipboardCheck, FaPaperPlane, FaCogs } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer, ConfirmOrderPayload, ConfirmOrderError } from '../services/OfferService';

interface OrderConfirmationModalProps {
  isOpen: boolean;
  offer: Offer;
  partnerEmail: string;
  onClose: () => void;
  onSent: () => void;
  onScrollToUnassigned?: () => void;
}

export default function OrderConfirmationModal({
  isOpen,
  offer,
  partnerEmail,
  onClose,
  onSent,
  onScrollToUnassigned,
}: OrderConfirmationModalProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [executionNotes, setExecutionNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [autoGenerateJobs, setAutoGenerateJobs] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setRecipientEmail(partnerEmail || '');
      setSubject(`Auftragsbestaetigung zu Angebot ${offer.offer_number}`);
      setMessage(getDefaultMessage(offer));
      setExecutionNotes('');
    }
  }, [isOpen, offer, partnerEmail]);

  const getDefaultMessage = (offer: Offer): string => {
    return `Sehr geehrte Damen und Herren,

vielen Dank fuer Ihren Auftrag!

Hiermit bestaetigen wir Ihren Auftrag basierend auf unserem Angebot ${offer.offer_number} mit dem Titel "${offer.title}".

Auftragssumme: ${OfferService.formatCurrency(offer.total_gross)} (inkl. MwSt.)

Die Auftragsbestaetigung finden Sie im Anhang.

Bei Fragen stehen wir Ihnen gerne zur Verfuegung.

Mit freundlichen Gruessen`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (sendEmail && !recipientEmail.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    setSending(true);
    try {
      const payload: ConfirmOrderPayload = {
        auto_generate_ops_jobs: autoGenerateJobs,
      };

      const response = await OfferService.confirmOrder(offer.id, payload);

      // Show success toast with job counts if jobs were generated
      if (response.ops_jobs && response.ops_jobs.created_tasks_count > 0) {
        const { created_tasks_count, created_checklist_items_count } = response.ops_jobs;
        toast.success(
          <div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Auftrag bestätigt.</strong> {created_tasks_count} Jobs mit {created_checklist_items_count} Subtasks ins Backlog erzeugt.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => navigate('/operations')}
                style={{
                  padding: '4px 10px',
                  background: '#0284c7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Zum Backlog
              </button>
              {offer.project_id && (
                <button
                  onClick={() => navigate(`/projects/${offer.project_id}`)}
                  style={{
                    padding: '4px 10px',
                    background: '#166534',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  Zum Projekt
                </button>
              )}
            </div>
          </div>,
          { autoClose: 8000 }
        );
      } else {
        toast.success(sendEmail
          ? 'Auftragsbestätigung erstellt und gesendet'
          : 'Auftragsbestätigung erstellt');
      }
      onSent();
    } catch (err: any) {
      console.error('Failed to confirm order:', err);
      const status = err?.response?.status;
      const errorData = err?.response?.data as ConfirmOrderError | undefined;
      const errorCode = errorData?.code;
      const errorDetail = errorData?.detail;

      if (status === 404) {
        toast.error('Angebot nicht gefunden.');
      } else if (status === 409) {
        switch (errorCode) {
          case 'positions_unassigned':
            toast.error(
              <div>
                <div style={{ marginBottom: '8px' }}>
                  Auftrag kann nicht bestätigt werden: noch Leistungen keinem Milestone zugeordnet.
                </div>
                {onScrollToUnassigned && (
                  <button
                    onClick={() => {
                      onClose();
                      onScrollToUnassigned();
                    }}
                    style={{
                      padding: '4px 10px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    Zu nicht zugeordneten Positionen
                  </button>
                )}
              </div>,
              { autoClose: 8000 }
            );
            break;
          case 'offer_missing_project':
            toast.error(
              <div>
                <div style={{ marginBottom: '8px' }}>
                  Bitte zuerst ein Projekt verknüpfen/erstellen, bevor Jobs erzeugt werden können.
                </div>
                {offer.business_partner_id && (
                  <button
                    onClick={() => navigate(`/partners/${offer.business_partner_id}`)}
                    style={{
                      padding: '4px 10px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    Zum Kunden
                  </button>
                )}
              </div>,
              { autoClose: 8000 }
            );
            break;
          case 'milestone_locked':
            toast.error('Mindestens ein Milestone ist bereits abgerechnet/bezahlt. Zuordnung ist gesperrt.');
            break;
          default:
            toast.error(errorDetail || 'Auftragsbestätigung konnte nicht erstellt werden');
        }
      } else {
        toast.error(errorDetail || 'Auftragsbestätigung konnte nicht erstellt werden');
      }
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '650px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            Auftragsbestaetigung (AB) erstellen
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div
            style={{
              padding: '12px 16px',
              background: '#f8f9fc',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #e0e3ef',
            }}
          >
            <strong>Angebot:</strong> {offer.offer_number} - {offer.title}
            <br />
            <strong>Betrag:</strong> {OfferService.formatCurrency(offer.total_gross)}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Ausfuehrungshinweise / Terminvorschlaege (optional)
            </label>
            <textarea
              value={executionNotes}
              onChange={(e) => setExecutionNotes(e.target.value)}
              rows={3}
              placeholder="z.B. Geplanter Ausfuehrungszeitraum: KW 12-14, Montage voraussichtlich am 20.03.2025..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
              Diese Hinweise erscheinen im AB-PDF und in der E-Mail an den Kunden.
            </p>
          </div>

          {/* Auto-generate jobs checkbox */}
          <div
            style={{
              padding: '16px',
              background: '#f8f9fc',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e0e3ef',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              <input
                type="checkbox"
                checked={autoGenerateJobs}
                onChange={(e) => setAutoGenerateJobs(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <FaCogs style={{ marginRight: '4px', color: '#666' }} />
              Automatisch Ops-Jobs aus Leistungen erzeugen
            </label>
            <p style={{ margin: '8px 0 0 28px', fontSize: '12px', color: '#666' }}>
              Jobs werden aus den zugeordneten Milestone-Positionen ins Backlog erstellt.
            </p>
          </div>

          <div
            style={{
              padding: '16px',
              background: '#f8f9fc',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              Auftragsbestaetigung per E-Mail senden
            </label>
          </div>

          {sendEmail && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Empfaenger E-Mail <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="kunde@beispiel.de"
                  style={inputStyle}
                  required={sendEmail}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Betreff <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Betreff der E-Mail"
                  style={inputStyle}
                  required={sendEmail}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Nachricht</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={8}
                  placeholder="Ihre Nachricht..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            </>
          )}

          <div
            style={{
              padding: '12px 16px',
              background: '#f8f9fc',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '13px',
              color: '#666',
              border: '1px solid #e0e3ef',
            }}
          >
            <FaClipboardCheck style={{ marginRight: '8px' }} />
            Es wird eine AB-Nummer generiert und ein PDF erstellt.
            {sendEmail && ' Das PDF wird als Anhang an die E-Mail angefuegt.'}
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={sending}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                border: 'none',
                borderRadius: '10px',
                background: sending ? '#ccc' : '#0284c7',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
            >
              {sendEmail ? <FaPaperPlane size={14} /> : <FaClipboardCheck size={14} />}
              {sending
                ? 'Wird erstellt...'
                : sendEmail
                  ? 'AB erstellen & senden'
                  : 'AB erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};
